import React, { useEffect, useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import PanelThemeToggle from "../../components/panel/PanelThemeToggle";
import TopbarNotificationsMenu from "../../components/TopbarNotificationsMenu";
import StatsJobBadge from "../../components/StatsJobBadge/StatsJobBadge";

import DoctorService from "../../services/doctorService";
import { DOCTOR_INFO_UPDATED_EVENT } from "../../utils/doctorInfoEmitter";
import { disconnectSocket } from "../../socket";
import { clearFileCache } from "../../services/doctorServices/FileService";
import { clearFormCache } from "../../services/doctorServices/FormService";
import { clearDoctorCache } from "../../services/doctorService";
import { clearCatalogCache, clearPatientListCache } from "../../services/patientService";
import { clearTemplateCache } from "../../services/doctorServices/ExcelImportService";
import { clearAllAuthSessions } from "../../utils/authSession";
import { translateSpecialization } from "../../utils/displayLabels";
import { PANEL } from "../../design-system/panel/panelTokens";
import {
  panelTopbarShellSx,
  panelTopbarInnerSx,
  panelTopbarMenuButtonSx,
  panelTopbarTitleSx,
  panelTopbarSubtitleSx,
  panelTopbarIconButtonSx,
  panelTopbarProfileChipSx,
  panelTopbarAvatarSx,
} from "../../design-system/panel/panelTopbarSx";

/** Doktor panel topbar — light panel teması */
export default function TopbarDoctor({ menuOpen, setMenuOpen }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const doctorService = useMemo(() => new DoctorService(), []);
  const [doctorInfo, setDoctorInfo] = useState(null);

  const getDateLocale = () => {
    const currentLang = i18n.language || "en";
    if (currentLang.startsWith("tr")) return "tr-TR";
    if (currentLang.startsWith("fr")) return "fr-FR";
    return "en-US";
  };

  const formattedDate = new Date().toLocaleDateString(getDateLocale(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const info = await doctorService.getDoctorInfo();
        setDoctorInfo(info);
        if (info?.doctor) {
          const { connectSocket } = await import("../../socket");
          connectSocket(info.doctor.id);
        }
      } catch (error) {
        console.error("Error fetching doctor info:", error);
      }
    };

    const handleDoctorInfoUpdated = (event) => {
      if (event.detail) {
        setDoctorInfo(event.detail);
      } else {
        fetchDoctorInfo();
      }
    };

    fetchDoctorInfo();
    window.addEventListener(DOCTOR_INFO_UPDATED_EVENT, handleDoctorInfoUpdated);
    return () => {
      window.removeEventListener(DOCTOR_INFO_UPDATED_EVENT, handleDoctorInfoUpdated);
    };
  }, [doctorService]);

  const handleLogout = () => {
    disconnectSocket();
    clearAllAuthSessions();
    localStorage.removeItem("languageManuallySet");
    clearFileCache();
    clearFormCache();
    clearDoctorCache();
    clearCatalogCache();
    clearPatientListCache();
    clearTemplateCache();
    navigate("/individual-login");
  };

  return (
    <Box sx={panelTopbarShellSx}>
      <Box sx={panelTopbarInnerSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <IconButton
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            sx={panelTopbarMenuButtonSx}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={panelTopbarTitleSx}>
              {t("welcome")}, {doctorInfo?.doctor?.name || t("doctor")}!
            </Typography>
            <Typography
              variant="caption"
              sx={{ ...panelTopbarSubtitleSx, display: { xs: "none", sm: "block" } }}
            >
              {formattedDate}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>

          <StatsJobBadge variant="panel" />
          <TopbarNotificationsMenu variant="panel" />
          <PanelThemeToggle />
          <LanguageSelector variant="panel" />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, borderColor: PANEL.border, display: { xs: "none", md: "block" } }}
          />

          <Tooltip title={t("profile")}>
            <Box onClick={() => navigate("/profile")} sx={panelTopbarProfileChipSx}>
              <Avatar sx={panelTopbarAvatarSx}>
                {doctorInfo?.doctor?.name?.charAt(0) || "D"}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: PANEL.text.primary,
                    fontSize: "0.85rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {doctorInfo?.doctor?.name} {doctorInfo?.doctor?.surname}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: PANEL.accent.text, fontSize: "0.7rem" }}
                >
                  {translateSpecialization(doctorInfo?.doctor?.specialization, t) ||
                    t("doctor")}
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          <Tooltip title={t("logout")}>
            <IconButton onClick={handleLogout} sx={panelTopbarIconButtonSx} aria-label={t("logout")}>
              <LogoutIcon sx={{ fontSize: 22, color: PANEL.danger.main }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
